<template>
  <span
    class="inline-title-field"
    :class="{
      'inline-title-field--editing': editing && !richText,
      'inline-title-field--pen-only': penOnly,
      'inline-title-field--muted': muted,
    }"
  >
    <span class="inline-title-field__anchor">
      <span
        v-if="!penOnly"
        class="inline-title-field__display"
        :title="fullDisplayText"
      >{{ displayText }}</span>
      <button
        v-if="editMode && !editing"
        type="button"
        class="inline-title-field__edit-btn"
        :aria-label="'Edit ' + fieldLabel"
        @click.stop="startEdit"
      >
        <i class="fas fa-pen" aria-hidden="true"></i>
      </button>
    </span>

    <div
      v-if="editing && !richText"
      ref="editorPopover"
      class="inline-title-field__editor-popover"
      :style="popoverStyle"
      @click.stop
    >
      <b-form-input
        v-model="draftValue"
        size="sm"
        class="inline-title-field__input"
        :placeholder="placeholder"
        autofocus
        @keyup.enter.native="save"
        @keyup.esc.native="cancel"
      />
      <div class="inline-title-field__actions">
        <button
          type="button"
          class="inline-title-field__action-btn inline-title-field__action-btn--save"
          title="Save"
          @click="save"
        >
          <i class="fas fa-check" aria-hidden="true"></i>
        </button>
        <button
          type="button"
          class="inline-title-field__action-btn"
          title="Cancel"
          @click="cancel"
        >
          <i class="fas fa-times" aria-hidden="true"></i>
        </button>
      </div>
    </div>

    <b-modal
      v-if="richText"
      v-model="editing"
      :id="modalId"
      size="md"
      :title="fieldLabel"
      modal-class="inline-title-field-modal"
      dialog-class="inline-title-field-modal-dialog"
      content-class="inline-title-field-modal-content"
      header-class="inline-title-field-modal-header py-2"
      body-class="inline-title-field-modal-body"
      footer-class="inline-title-field-modal-footer py-2"
      centered
      no-close-on-backdrop
      @hidden="onRichModalHidden"
    >
      <rich-text-editor
        v-model="draftValue"
        inline
        compact
        class="inline-title-field__rich"
      />
      <template #modal-footer>
        <b-button size="sm" variant="outline-secondary" @click="cancel">
          Cancel
        </b-button>
        <b-button size="sm" variant="primary" @click="save">
          Save
        </b-button>
      </template>
    </b-modal>
  </span>
</template>

<script>
import RichTextEditor from "@/components/RichTextEditor.vue";

export default {
  name: "InlineTitleField",
  components: { RichTextEditor },
  props: {
    value: { type: String, default: "" },
    editMode: { type: Boolean, default: false },
    fieldLabel: { type: String, default: "Title" },
    placeholder: { type: String, default: "" },
    richText: { type: Boolean, default: false },
    penOnly: { type: Boolean, default: false },
    muted: { type: Boolean, default: false },
  },
  data() {
    return {
      editing: false,
      draftValue: "",
      snapshot: "",
      popoverStyle: {},
    };
  },
  computed: {
    modalId() {
      return "inline-title-field-" + this._uid;
    },
    fullDisplayText() {
      const v = (this.value || "").trim();
      if (!v) return this.placeholder || this.fieldLabel;
      if (this.richText) {
        return v.replace(/<[^>]+>/g, "").trim() || this.placeholder;
      }
      return v;
    },
    displayText() {
      const v = this.fullDisplayText;
      if (this.placeholder && v === this.placeholder) {
        return this.placeholder;
      }
      return v || this.placeholder || "—";
    },
  },
  watch: {
    editMode(on) {
      if (!on && this.editing) this.cancel();
    },
  },
  beforeDestroy() {
    this.detachPopoverListeners();
  },
  methods: {
    startEdit() {
      this.snapshot = this.value || "";
      this.draftValue = this.value || "";
      this.editing = true;
      this.$emit("edit-start");
      if (!this.richText) {
        this.$nextTick(() => {
          this.updatePopoverPosition();
          this.attachPopoverListeners();
        });
      }
    },
    attachPopoverListeners() {
      this.detachPopoverListeners();
      this._onReposition = () => this.updatePopoverPosition();
      window.addEventListener("scroll", this._onReposition, true);
      window.addEventListener("resize", this._onReposition, { passive: true });
    },
    detachPopoverListeners() {
      if (this._onReposition) {
        window.removeEventListener("scroll", this._onReposition, true);
        window.removeEventListener("resize", this._onReposition);
        this._onReposition = null;
      }
    },
    updatePopoverPosition() {
      const pop = this.$refs.editorPopover;
      if (!pop) return;
      const anchor = this.$el.querySelector(".inline-title-field__anchor") || this.$el;
      const rect = anchor.getBoundingClientRect();
      const width = 280;
      let left = rect.left;
      if (left + width > window.innerWidth - 12) {
        left = window.innerWidth - width - 12;
      }
      if (left < 12) left = 12;
      let top = rect.bottom + 6;
      const estimatedHeight = 48;
      if (top + estimatedHeight > window.innerHeight - 12) {
        top = Math.max(12, rect.top - estimatedHeight - 6);
      }
      this.popoverStyle = {
        position: "fixed",
        top: `${top}px`,
        left: `${left}px`,
        width: `${width}px`,
        zIndex: 10050,
      };
    },
    onRichModalHidden() {
      if (this._savingRich) {
        this._savingRich = false;
        return;
      }
      if (this.editing) this.cancel();
    },
    save() {
      const next = this.draftValue == null ? "" : String(this.draftValue);
      if (this.richText) this._savingRich = true;
      this.detachPopoverListeners();
      this.$emit("input", next);
      this.$emit("save", next);
      this.editing = false;
    },
    cancel() {
      this.draftValue = this.snapshot;
      this.detachPopoverListeners();
      this.editing = false;
    },
  },
};
</script>

<style scoped>
.inline-title-field {
  position: relative;
  display: inline-flex;
  align-items: center;
  gap: 3px;
  flex-shrink: 0;
  max-width: 100%;
  vertical-align: middle;
  line-height: 1.2;
}

.inline-title-field__anchor {
  display: inline-flex;
  align-items: center;
  gap: 3px;
}

.inline-title-field--pen-only {
  gap: 0;
}

.inline-title-field__display {
  font-size: inherit;
  font-weight: inherit;
  color: inherit;
  max-width: 220px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.inline-title-field--muted .inline-title-field__display {
  font-size: 0.85em;
  opacity: 0.9;
}

.inline-title-field__edit-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border: none;
  background: transparent;
  color: #64748b;
  padding: 0 2px;
  margin: 0;
  line-height: 1;
  cursor: pointer;
  font-size: 0.7rem;
  flex-shrink: 0;
  vertical-align: middle;
}

.inline-title-field__edit-btn:hover {
  color: #2563eb;
}

.inline-title-field--pen-only .inline-title-field__edit-btn {
  font-size: 0.72rem;
  padding: 0 3px;
}

.inline-title-field__editor-popover {
  display: flex;
  flex-wrap: nowrap;
  align-items: center;
  gap: 6px;
  padding: 6px 8px;
  background: #fff;
  border: 1px solid #e2e8f0;
  border-radius: 6px;
  box-shadow: 0 8px 24px rgba(15, 23, 42, 0.18);
}

.inline-title-field__input {
  flex: 1 1 auto;
  min-width: 0;
}

.inline-title-field__rich {
  width: 100%;
}

.inline-title-field__actions {
  display: inline-flex;
  flex-shrink: 0;
  gap: 4px;
  align-items: center;
}

.inline-title-field__action-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 22px;
  height: 22px;
  padding: 0;
  border: 1px solid #e2e8f0;
  border-radius: 4px;
  background: #f8fafc;
  color: #475569;
  font-size: 0.65rem;
  cursor: pointer;
}

.inline-title-field__action-btn:hover {
  background: #f1f5f9;
}

.inline-title-field__action-btn--save {
  color: #15803d;
  border-color: #bbf7d0;
  background: #f0fdf4;
}

.inline-title-field__action-btn--save:hover {
  background: #dcfce7;
}
</style>

<style>
/* Unscoped: b-modal is teleported to body */
.inline-title-field-modal {
  z-index: 10060 !important;
}

.inline-title-field-modal-dialog {
  max-width: 520px;
}

.inline-title-field-modal-body {
  padding: 0.75rem 1rem;
  overflow: visible;
}

.inline-title-field-modal-body .inline-title-field__rich,
.inline-title-field-modal-body .inline-rich-text-editor {
  width: 100%;
}

.inline-title-field-modal-body .inline-rich-text-editor .ql-toolbar {
  border-radius: 4px 4px 0 0;
  background: #f8fafc;
}

.inline-title-field-modal-body .inline-rich-text-editor .ql-container {
  border-radius: 0 0 4px 4px;
  background: #fff;
}

.inline-title-field-modal-footer {
  border-top: 1px solid #e2e8f0;
  background: #f8fafc;
}
</style>
